// AXIOM — immutable, score-relevant proof for a completed run.
//
// The proof is deliberately separate from the ranking UI.  It is a compact
// snapshot of the run, hashed with the SDK's digestOf(), and handed to the
// hosted submitScore call as custom proof data.  A missing digest is a hard
// stop: there is no local or HTTP fallback that could turn an unbound score
// into a trusted submission.

import { digestOf, newUuid } from '../tryportal/index.js';

export const RUN_SCORE_PROOF_SCHEMA_VERSION = 1;
// Keep the proof module usable in Node's standard ESM test runner as well as
// Vite's browser bundle. These values mirror manifest.score and are validated
// by the integration verifier against the committed manifest.
const SCORE_CONTRACT = Object.freeze({ metricKey: 'magnitude', minValue: 0, maxValue: 100000000 });

const INTEGER_FIELDS = [
  'durationMs', 'seed', 'wave', 'level', 'kills', 'shotsFired', 'shotsHit',
  'bestChain', 'primeCrits', 'peakDamage',
];

function boundedInteger(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function scoreFromState(state) {
  const peak = state?.peakDamage;
  const magnitude = peak && typeof peak.logAbs10 === 'function' ? peak.logAbs10() : 0;
  return boundedInteger(magnitude, SCORE_CONTRACT.minValue,
    Math.min(Number.MAX_SAFE_INTEGER, SCORE_CONTRACT.maxValue));
}

function canonicalValue(value) {
  if (Array.isArray(value)) return value.map(canonicalValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonicalValue(value[key])]));
  }
  return value;
}

export function canonicalProofString(proof) {
  return JSON.stringify(canonicalValue(proof));
}

function freezeDeep(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const child of Object.values(value)) freezeDeep(child);
  return value;
}

export function proofSnapshot(state) {
  const perks = Array.isArray(state?.perks) ? state.perks.map(String).sort() : [];
  return {
    schemaVersion: RUN_SCORE_PROOF_SCHEMA_VERSION,
    metricKey: SCORE_CONTRACT.metricKey,
    score: scoreFromState(state),
    durationMs: boundedInteger((Number(state?.runTime) || 0) * 1000, 0, 86400000),
    seed: boundedInteger(state?.seed, 0, 0xffffffff),
    wave: boundedInteger(state?.wave),
    level: boundedInteger(state?.level),
    kills: boundedInteger(state?.kills),
    shotsFired: boundedInteger(state?.shotsFired),
    shotsHit: boundedInteger(state?.shotsHit),
    bestChain: boundedInteger(state?.bestChain),
    primeCrits: boundedInteger(state?.primeCrits),
    peakDamage: scoreFromState(state),
    perks,
  };
}

export async function createRunScoreProof(state, options = {}) {
  const snapshot = proofSnapshot(state);
  const makeUuid = options.newUuid || newUuid;
  const id = options.clientSubmissionId || makeUuid();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)) {
    throw new TypeError('clientSubmissionId must be a UUID v4');
  }
  const digest = options.digestOf || digestOf;
  const stateDigest = await digest(canonicalProofString(snapshot));
  if (!/^[a-f0-9]{64}$/.test(stateDigest)) {
    throw new Error('state digest was not a SHA-256 hex digest');
  }
  const result = {
    score: snapshot.score,
    stateDigest,
    clientSubmissionId: id,
    proof: snapshot,
  };
  return freezeDeep(result);
}

export function isScoreTransportLoss(error) {
  if (error?.name === 'TRYPORTALError' && error?.code === 'SCORE_UNREACHABLE') return true;
  // DirectTransport preserves a native fetch TypeError when the connection is
  // lost before an HTTP response exists. Only transport-shaped messages qualify;
  // validation/programming TypeErrors remain permanently non-retryable.
  const message = String(error?.message || '').toLowerCase();
  return error instanceof TypeError && /(fetch|network|connect|timeout|load)/.test(message);
}
